<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';

export default {
  components: {
    Thumbnail,
  },
  props: {
    agentList: {
      type: Array,
      default: () => [],
    },
    selectedAgents: {
      type: Array,
      default: () => [],
    },
    updateSelectedAgents: {
      type: Function,
      default: () => {},
    },
    isWorking: {
      type: Boolean,
      default: false,
    },
    submitButtonText: {
      type: String,
      default: '',
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    return { uiSettings, updateUISettings };
  },
  data() {
    return {
      selectedInboxIds: [],
      inboxMemberAgentIds: [],
      isHydratingInboxFilter: false,
    };
  },
  computed: {
    ...mapGetters({
      inboxList: 'inboxes/getInboxes',
    }),
    teamId() {
      return this.$route.params.teamId;
    },
    selectedInboxes: {
      get() {
        return this.inboxList.filter(inbox =>
          this.selectedInboxIds.includes(inbox.id)
        );
      },
      set(inboxes) {
        this.selectedInboxIds = inboxes.map(inbox => inbox.id);
      },
    },
    filteredAgentList() {
      const hasInboxFilter = this.selectedInboxIds.length > 0;

      if (!hasInboxFilter) {
        return this.agentList;
      }

      const filteredAgents = this.agentList.filter(agent => {
        const matchesInboxFilter =
          !hasInboxFilter || this.inboxMemberAgentIds.includes(agent.id);

        return matchesInboxFilter;
      });

      const selectedAgentsOutsideFilter = this.agentList.filter(
        agent =>
          this.selectedAgents.includes(agent.id) &&
          !filteredAgents.some(filteredAgent => filteredAgent.id === agent.id)
      );

      return [...selectedAgentsOutsideFilter, ...filteredAgents];
    },
    selectedAgentCount() {
      return this.selectedAgents.length;
    },
    allAgentsSelected() {
      if (!this.filteredAgentList.length) {
        return false;
      }

      return this.filteredAgentList.every(agent =>
        this.selectedAgents.includes(agent.id)
      );
    },
    disableSubmitButton() {
      return this.selectedAgentCount === 0;
    },
  },
  watch: {
    selectedInboxIds: {
      async handler() {
        if (this.isHydratingInboxFilter) {
          return;
        }
        this.saveInboxFilterSettings();
        await this.fetchInboxMembersForSelectedInboxes();
      },
      deep: true,
    },
  },
  async mounted() {
    await this.$store.dispatch('inboxes/get');
    this.initializeInboxFilterFromSettings();
  },
  methods: {
    initializeInboxFilterFromSettings() {
      const storedInboxFilter =
        this.uiSettings?.teams_agents_inbox_filter?.[this.teamId] || [];

      this.isHydratingInboxFilter = true;
      this.selectedInboxIds = storedInboxFilter;

      this.fetchInboxMembersForSelectedInboxes().finally(() => {
        this.isHydratingInboxFilter = false;
      });
    },
    saveInboxFilterSettings() {
      const currentInboxFilters =
        this.uiSettings?.teams_agents_inbox_filter || {};
      this.updateUISettings({
        teams_agents_inbox_filter: {
          ...currentInboxFilters,
          [this.teamId]: this.selectedInboxIds,
        },
      });
    },
    async fetchInboxMembersForSelectedInboxes() {
      if (!this.selectedInboxIds.length) {
        this.inboxMemberAgentIds = [];
        return;
      }

      const responses = await Promise.allSettled(
        this.selectedInboxIds.map(inboxId =>
          this.$store.dispatch('inboxMembers/get', {
            inboxId,
          })
        )
      );

      const uniqueAgentIds = new Set();
      responses.forEach(response => {
        if (response.status !== 'fulfilled') {
          return;
        }

        const inboxMembers = response.value?.data?.payload || [];
        inboxMembers.forEach(member => uniqueAgentIds.add(member.id));
      });

      this.inboxMemberAgentIds = [...uniqueAgentIds];
    },
    isAgentSelected(agentId) {
      return this.selectedAgents.includes(agentId);
    },
    handleSelectAgent(agentId) {
      const shouldRemove = this.isAgentSelected(agentId);

      let result = [];
      if (shouldRemove) {
        result = this.selectedAgents.filter(item => item !== agentId);
      } else {
        result = [...this.selectedAgents, agentId];
      }

      this.updateSelectedAgents(result);
    },
    selectAllAgents() {
      const visibleAgentIds = this.filteredAgentList.map(item => item.id);
      const hiddenSelectedAgentIds = this.selectedAgents.filter(
        selectedAgentId =>
          !this.filteredAgentList.some(agent => agent.id === selectedAgentId)
      );
      const result = [...hiddenSelectedAgentIds, ...visibleAgentIds];
      this.updateSelectedAgents(result);
    },
    agentRowClass(agentId) {
      return { 'is-active': this.isAgentSelected(agentId) };
    },
  },
};
</script>

<template>
  <div>
    <div class="add-agents__header" />
    <label
      class="block mb-2 text-sm font-medium text-slate-900 dark:text-slate-100"
    >
      {{ $t('INBOX_MGMT.HEADER') }}
    </label>
    <multiselect
      v-model="selectedInboxes"
      :options="inboxList"
      track-by="id"
      label="name"
      multiple
      :close-on-select="false"
      :clear-on-select="false"
      hide-selected
      :placeholder="$t('FORMS.MULTISELECT.SELECT')"
      :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
      :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
      class="mb-4"
    />
    <table class="woot-table">
      <thead>
        <tr>
          <td class="ltr:pl-2.5 rtl:pr-2.5">
            <div class="flex items-center">
              <input
                name="select-all-agents"
                type="checkbox"
                :checked="allAgentsSelected ? 'checked' : ''"
                :title="$t('TEAMS_SETTINGS.AGENTS.SELECT_ALL')"
                @click.self="selectAllAgents"
              />
            </div>
          </td>
          <td class="text-slate-800 dark:text-slate-100 ltr:pl-2.5 rtl:pr-2.5">
            {{ $t('TEAMS_SETTINGS.AGENTS.AGENT') }}
          </td>
          <td class="text-slate-800 dark:text-slate-100 ltr:pl-2.5 rtl:pr-2.5">
            {{ $t('TEAMS_SETTINGS.AGENTS.EMAIL') }}
          </td>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="agent in filteredAgentList"
          :key="agent.id"
          :class="agentRowClass(agent.id)"
        >
          <td class="w-12">
            <div class="flex items-center">
              <input
                type="checkbox"
                :checked="isAgentSelected(agent.id)"
                @click.self="() => handleSelectAgent(agent.id)"
              />
            </div>
          </td>
          <td>
            <div class="flex items-center gap-2">
              <Thumbnail
                :src="agent.thumbnail"
                size="24px"
                :username="agent.name"
                :status="agent.availability_status"
              />
              <h4 class="text-base mb-0 text-slate-800 dark:text-slate-100">
                {{ agent.name }}
              </h4>
            </div>
          </td>
          <td>
            {{ agent.email || '---' }}
          </td>
        </tr>
      </tbody>
    </table>
    <div class="flex items-center justify-between mt-2">
      <p>
        {{
          $t('TEAMS_SETTINGS.AGENTS.SELECTED_COUNT', {
            selected: selectedAgents.length,
            total: filteredAgentList.length,
          })
        }}
      </p>
      <woot-submit-button
        :button-text="submitButtonText"
        :loading="isWorking"
        :disabled="disableSubmitButton"
      />
    </div>
  </div>
</template>

<style scoped>
input {
  @apply mb-0;
}
</style>
